import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { AlertTriangle, CheckCircle2, Circle, Loader2, Lock, Rocket, Undo2 } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import type { StoreProfileDetail, TwinReadinessStepStatus } from '../types';
import { TWIN_READINESS_STATE_LABELS } from '../lib/twinReadiness';
import { ProvenanceBadge } from './ProvenanceBadge';

interface TwinReadinessPanelProps {
  profile: StoreProfileDetail;
  storeId?: number;
  storeIdParam?: string;
  /** Jump to the place where a blocked/action-needed step can be resolved */
  onStepAction?: (stepId: string) => void;
}

function StepIcon({ status }: { status: TwinReadinessStepStatus }) {
  if (status === 'complete') {
    return <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" aria-hidden />;
  }
  if (status === 'blocked') {
    return <Lock className="h-4 w-4 text-gray-300 shrink-0 mt-0.5" aria-hidden />;
  }
  return <Circle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" aria-hidden />;
}

/**
 * Requirement-based readiness checklist — one model behind the header, the index
 * and this panel, so percent/state can never contradict the steps shown here.
 */
export function TwinReadinessPanel({ profile, storeId, storeIdParam, onStepAction }: TwinReadinessPanelProps) {
  const readiness = profile.twinReadiness;
  const [isPublishing, setIsPublishing] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const canPublish = storeId != null && storeIdParam != null;
  const isPublished = Boolean(profile.twinPublishedAt);

  const togglePublish = async () => {
    if (!canPublish || isPublishing) return;
    setIsPublishing(true);
    try {
      const res = await apiRequest(
        `/api/store-profiles/${storeId}/floorplan/twin-publish`,
        'POST',
        { publish: !isPublished },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? 'Failed to update twin publish state');
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['/api/store-profiles', storeIdParam, 'context'] }),
        queryClient.invalidateQueries({ queryKey: ['/api/stores', storeIdParam, 'detail'] }),
        queryClient.invalidateQueries({ queryKey: ['/api/store-profiles/summary'] }),
      ]);
      toast({
        title: isPublished ? 'Store tour hidden from retailers' : 'Store tour available to retailers',
        description: isPublished
          ? 'Retailers will no longer see this store as fully available.'
          : 'Retailers can now explore this store with confidence.',
      });
    } catch (error) {
      toast({
        title: 'Could not update publish state',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex flex-wrap items-center gap-2">
          Twin setup checklist
          <ProvenanceBadge provenance="derived" />
          <Badge variant="secondary">{readiness.completedCount} of {readiness.totalCount}</Badge>
          <Badge variant="outline">{TWIN_READINESS_STATE_LABELS[readiness.state]}</Badge>
        </CardTitle>
        <p className="text-sm text-gray-500">
          Completeness is calculated from these requirements — nothing is counted twice.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <Progress
          value={readiness.percent}
          className="h-2"
          aria-label={`Twin readiness ${readiness.percent} percent — ${readiness.completedCount} of ${readiness.totalCount} requirements met`}
        />

        <ol className="space-y-2.5">
          {readiness.steps.map((step, index) => {
            const isNext = readiness.nextAction?.stepId === step.id;
            return (
              <li
                key={step.id}
                className={`flex items-start gap-3 rounded-lg border px-3 py-2 ${
                  isNext ? 'border-amber-300 bg-amber-50/60' : 'border-transparent'
                }`}
              >
                <span className="text-xs text-gray-400 mt-1 w-4 shrink-0 tabular-nums">{index + 1}</span>
                <StepIcon status={step.status} />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium">{step.label}</p>
                    {isNext && (
                      <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]">
                        Next action
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">{step.detail}</p>
                </div>
                {step.status === 'action_needed' && onStepAction && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="shrink-0"
                    onClick={() => onStepAction(step.id)}
                  >
                    Go
                  </Button>
                )}
              </li>
            );
          })}
        </ol>

        {readiness.state === 'ready_to_publish' && canPublish && (
          <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-sm text-emerald-800">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
            All setup steps complete — review and make available to retailers when ready.
          </div>
        )}

        {canPublish && (
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <Button
              type="button"
              size="sm"
              variant={isPublished ? 'outline' : 'default'}
              onClick={() => void togglePublish()}
              disabled={isPublishing}
            >
              {isPublishing ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" aria-hidden />
              ) : isPublished ? (
                <Undo2 className="h-4 w-4 mr-1" aria-hidden />
              ) : (
                <Rocket className="h-4 w-4 mr-1" aria-hidden />
              )}
              {isPublished ? 'Hide from retailers' : 'Make available to retailers'}
            </Button>
            {profile.twinPublishedAt && (
              <p className="text-xs text-gray-500">
                Published {profile.twinPublishedAt.slice(0, 10)}
                {profile.twinPublishedByName ? ` by ${profile.twinPublishedByName}` : ''}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
